/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1790208000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Sep 24 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "zendesk",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Zendesk",
          "depth": 0,
          "id": "connect-to-zendesk"
        }, {
          "value": "Sign in with Zendesk (recommended)",
          "depth": 1,
          "id": "sign-in-with-zendesk-recommended"
        }, {
          "value": "API token",
          "depth": 1,
          "id": "api-token"
        }, {
          "value": "Migrate from API token to OAuth",
          "depth": 1,
          "id": "migrate-from-api-token-to-oauth"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Users",
          "depth": 1,
          "id": "users"
        }, {
          "value": "Organizations",
          "depth": 1,
          "id": "organizations"
        }, {
          "value": "Tickets",
          "depth": 1,
          "id": "tickets"
        }, {
          "value": "Organization Memberships",
          "depth": 1,
          "id": "organization-memberships"
        }, {
          "value": "Custom objects",
          "depth": 1,
          "id": "custom-objects"
        }, {
          "value": "Legacy custom objects",
          "depth": 1,
          "id": "legacy-custom-objects"
        }, {
          "value": "Configure delete behavior",
          "depth": 1,
          "id": "configure-delete-behavior"
        }, {
          "value": "Configure search batch size",
          "depth": 1,
          "id": "configure-search-batch-size"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      h3: "h3",
      code: "code",
      img: "img",
      ul: "ul",
      li: "li",
      h4: "h4",
      em: "em"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Zendesk is a customer service platform for managing support tickets and customer profiles. Hightouch syncs users, organizations, tickets, custom objects, and organization memberships from your warehouse to Zendesk, so agents work with customer data that's current and complete."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Sync Type"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            }), _jsx(_components.th, {
              align: "left",
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              align: "left",
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#users",
                children: _jsx(_components.strong, {
                  children: "Users"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Create and update end users and agents"
            }), _jsx(_components.td, {
              align: "left",
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.zendesk.com/api-reference/ticketing/users/users/#create-or-update-many-users",
                children: "Create or update many users →"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#organizations",
                children: _jsx(_components.strong, {
                  children: "Organizations"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Create and update the organizations your users belong to"
            }), _jsx(_components.td, {
              align: "left",
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.zendesk.com/api-reference/ticketing/organizations/organizations/",
                children: "Create or update organizations →"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#tickets",
                children: _jsx(_components.strong, {
                  children: "Tickets"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Create and update support tickets"
            }), _jsx(_components.td, {
              align: "left",
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.zendesk.com/api-reference/ticketing/tickets/tickets/",
                children: "Create and update many tickets →"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#organization-memberships",
                children: _jsx(_components.strong, {
                  children: "Organization Memberships"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Add users to organizations"
            }), _jsx(_components.td, {
              align: "left",
              children: "Upsert"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.zendesk.com/api-reference/ticketing/organizations/organization_memberships/",
                children: "Create many memberships →"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#custom-objects",
                children: _jsx(_components.strong, {
                  children: "Custom objects"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Create and update records of the custom objects defined in your account"
            }), _jsx(_components.td, {
              align: "left",
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.zendesk.com/api-reference/custom-data/custom-objects/custom_object_records/",
                children: "Manage custom object records →"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#legacy-custom-objects",
                children: _jsx(_components.strong, {
                  children: "Legacy custom objects"
                })
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Create and update records of legacy (Sunshine) custom objects"
            }), _jsx(_components.td, {
              align: "left",
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "https://developer.zendesk.com/api-reference/custom-data/custom-objects-api/resources/",
                children: "Manage legacy object records →"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-zendesk",
        children: "Connect to Zendesk"
      }), "\n", _jsxs(_components.p, {
        children: ["Connect with ", _jsx(_components.strong, {
          children: "Sign in with Zendesk"
        }), " (OAuth, recommended) or a Zendesk API token."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Zendesk is ", _jsx(_components.a, {
            href: "https://support.zendesk.com/hc/en-us/articles/10851263566234-Announcing-the-removal-of-API-tokens-as-an-authentication-method-for-API-requests",
            children: "retiring API token\nauthentication"
          }), ":\nnew API tokens can't be created after October 27, 2026, and all remaining\ntokens stop working on April 30, 2027. If your destination uses an API token,\n", _jsx(_components.a, {
            href: "#migrate-from-api-token-to-oauth",
            children: "migrate to OAuth"
          }), " before then to keep your\nsyncs running."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "sign-in-with-zendesk-recommended",
        children: "Sign in with Zendesk (recommended)"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Sign in with Zendesk"
        }), ", enter your Zendesk subdomain—for example, ", _jsx(_components.code, {
          children: "mycompany"
        }), " if your Zendesk URL is ", _jsx(_components.code, {
          children: "mycompany.zendesk.com"
        }), "—and complete the sign-in. The account you sign in with must be a Zendesk admin, since the connection needs read and write access to the API."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-oauth.png",
          alt: ""
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Zendesk issues Hightouch short-lived access tokens that refresh automatically.\nYou don't need to rotate any credentials."
        })
      }), "\n", _jsx(_components.h3, {
        id: "api-token",
        children: "API token"
      }), "\n", _jsxs(_components.p, {
        children: ["Zendesk is retiring this method; use ", _jsx(_components.a, {
          href: "#sign-in-with-zendesk-recommended",
          children: "Sign in with Zendesk"
        }), " instead."]
      }), "\n", _jsxs(_components.p, {
        children: ["In Zendesk, go to ", _jsx(_components.strong, {
          children: "Admin Center"
        }), " > ", _jsx(_components.strong, {
          children: "Apps and integrations"
        }), " > ", _jsx(_components.strong, {
          children: "Zendesk API"
        }), " and add a new API token for Hightouch. Make sure ", _jsx(_components.strong, {
          children: "Token access"
        }), " is enabled."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-api-token.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Copy the API token and fill in the fields in the Hightouch destination setup:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "API token:"
          }), " the token from Zendesk"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Email:"
          }), " the email you use to log in to Zendesk"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Support URL:"
          }), " the URL of your Zendesk support site, including ", _jsx(_components.code, {
            children: "https://"
          }), "—for example, ", _jsx(_components.code, {
            children: "https://hightouch.zendesk.com"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "migrate-from-api-token-to-oauth",
        children: "Migrate from API token to OAuth"
      }), "\n", _jsxs(_components.p, {
        children: ["Open the destination's ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab in Hightouch, select ", _jsx(_components.strong, {
          children: "Sign in with Zendesk"
        }), ", and complete the sign-in as a Zendesk admin. Your syncs and field mappings don't change."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "users",
        children: "Users"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Upsert"
        }), " when Hightouch should create missing users and update existing ones. Use ", _jsx(_components.strong, {
          children: "Update"
        }), " when Hightouch should only modify users that already exist."]
      }), "\n", _jsxs(_components.p, {
        children: ["Zendesk may send a verification email when a sync creates a user, depending on your workspace settings. To suppress it, keep the default option that marks created users as verified; choose ", _jsx(_components.strong, {
          children: "Use the Zendesk default behavior"
        }), " if you want Zendesk to decide whether to send verification emails."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Match rows from your model to Zendesk users on external ID, Zendesk ID, email, or a custom field."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["Map columns to Zendesk's default user fields or to custom fields. To add custom fields to users, refer to the ", _jsx(_components.a, {
          href: "https://support.zendesk.com/hc/en-us/articles/203662066",
          children: "Zendesk docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "organizations",
        children: "Organizations"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Upsert"
        }), " to create and update organizations, or ", _jsx(_components.strong, {
          children: "Update"
        }), " to only modify existing ones. Match on external ID, Zendesk ID, or a custom field, and map columns to default or custom organization fields."]
      }), "\n", _jsx(_components.h3, {
        id: "tickets",
        children: "Tickets"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Upsert"
        }), " to create and update tickets, or ", _jsx(_components.strong, {
          children: "Update"
        }), " to only modify existing ones. Match on external ID, Zendesk ID, or a custom field, and map columns to default or custom ticket fields."]
      }), "\n", _jsx(_components.h3, {
        id: "organization-memberships",
        children: "Organization Memberships"
      }), "\n", _jsxs(_components.p, {
        children: ["Organization memberships add users to organizations, so records are matched by both the ", _jsx(_components.code, {
          children: "user_id"
        }), " ", _jsx(_components.em, {
          children: "and"
        }), " ", _jsx(_components.code, {
          children: "organization_id"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-org-membership-id.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The matching field should combine the user and organization columns you use in the field mappings. For example, if you match users by ", _jsx(_components.code, {
          children: "email"
        }), " and organizations by ", _jsx(_components.code, {
          children: "external_id"
        }), ", this field should be ", _jsx(_components.code, {
          children: "user_email:org_externalId"
        }), ". Your model should look similar to this:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-org-membership-query-ex.png",
          alt: ""
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Don't edit the user or organization value of an existing membership row. To\nchange a membership, remove the old row from your model and add a new one."
        })
      }), "\n", _jsx(_components.h3, {
        id: "custom-objects",
        children: "Custom objects"
      }), "\n", _jsxs(_components.p, {
        children: ["If your Zendesk account has ", _jsx(_components.a, {
          href: "https://support.zendesk.com/hc/en-us/articles/5914453843994-Understanding-custom-objects",
          children: "custom objects"
        }), ", they appear in the object dropdown with the prefix ", _jsx(_components.strong, {
          children: "(Custom)"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-custom-objects.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Map columns to any property in the object's schema."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-custom-mapping.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can also create associations between existing object records using a predetermined relationship type; the associations appear in the field mappings. For more details, refer to the ", _jsx(_components.a, {
          href: "https://developer.zendesk.com/api-reference/custom-data/custom-objects-api/relationships/",
          children: "Zendesk docs"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "legacy-custom-objects",
        children: "Legacy custom objects"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://support.zendesk.com/hc/en-us/articles/4408834725402",
          children: "Legacy custom objects"
        }), " appear in the object dropdown with the prefix ", _jsx(_components.strong, {
          children: "(Legacy custom)"
        }), ". Zendesk is sunsetting legacy custom objects in favor of the current custom objects framework, so prefer ", _jsx(_components.a, {
          href: "#custom-objects",
          children: "custom objects"
        }), " for new syncs."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-delete-behavior",
        children: "Configure delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, you can delete the Zendesk record or clear its synced fields when a row leaves your model. In ", _jsx(_components.strong, {
          children: "Update"
        }), " mode, clearing synced fields is the only delete option. Organization membership syncs can delete the membership."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-search-batch-size",
        children: "Configure search batch size"
      }), "\n", _jsx(_components.p, {
        children: "Zendesk's search API can return up to 1,000 results, but by default Hightouch searches for 100 records at a time. You can increase this up to 500 for a faster sync."
      }), "\n", _jsxs(_components.p, {
        children: ["Larger batches increase the risk of a ", _jsx(_components.a, {
          href: "https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/414",
          children: _jsx(_components.code, {
            children: "414 - Long URI"
          })
        }), " error. If you see these errors, lower the batch size."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-zendesk-batch-form.png",
          alt: "Setting batch size in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Zendesk, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes: other\ndestinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Synced users and organizations appear in Zendesk under ", _jsx(_components.strong, {
          children: "Customers"
        }), " and ", _jsx(_components.strong, {
          children: "Organizations"
        }), " in the agent workspace, and a user's organization memberships appear on their profile. Custom object records appear in ", _jsx(_components.strong, {
          children: "Admin Center"
        }), " > ", _jsx(_components.strong, {
          children: "Objects and rules"
        }), " > ", _jsx(_components.strong, {
          children: "Custom objects"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
